<template>
    <div>
        <div
            class="form-group justify-content-center align-items-start"
            :class="{ 'form-row': formRow, 'row': !formRow }"
        >
            <div class="col-md-3">
                <label
                    for="password"
                    v-if='registration'
                    v-translate
                >Password</label>
                <label
                    for="password"
                    v-else
                    v-translate
                >New Password</label>
            </div>

            <div class="col-md-6">
                <input
                    @keypress="checkInput"
                    v-validate="'required'"
                    id="password"
                    :type="pwordHidden ? 'password' : 'text'"
                    class="form-control is-password"
                    :class="{ 'is-invalid': passwordSubmitFailed }"
                    name="password"
                    :disabled="formSubmitting ? true : false"
                    v-model="password"
                    required
                >
                <InlineError
                    :errors="errors"
                    type="password"
                />

                <div
                    tabindex="0"
                    class="eye-container"
                    @click="togglePassword"
                    @keyup.enter.space="togglePassword"
                    aria-label="Reveals or hides password"
                >
                    <object
                        tabindex="-1"
                        v-show="pwordHidden"
                        data="/img/eye.svg"
                        type="image/svg+xml"
                        alt="Password hidden"
                    ></object>
                    <object
                        tabindex="-1"
                        v-show="!pwordHidden"
                        data="/img/eye-filled.svg"
                        type="image/svg+xml"
                        alt="Password revealed"
                    ></object>
                </div>
            </div>
        </div>

        <div
            class="justify-content-center my-3"
            :class="{ 'form-row': formRow, 'row': !formRow }"
        >
            <div class="col-md-6 offset-md-3">
                <ul class="password-validation-bars">
                    <li
                        :style="password.length > 0 ? 'border: none;' : 'background-color: transparent;'"
                        class="password-validation-bar password-validation-bar-1"
                    ></li>
                    <li
                        :style="total > 1  ? 'border: none;' : 'background-color: transparent;'"
                        class="password-validation-bar password-validation-bar-2"
                    ></li>
                    <li
                        :style="total >= 4 && validations.notMatchedPassword && validations.notMatchedPassword !== 'interim' ? 'border: none;' : 'background-color: transparent;'"
                        class="password-validation-bar password-validation-bar-3"
                    ></li>
                </ul>
                <ul class="password-validation-list">
                    <li
                        :class="validations.atLeast8 ? 'li-valid' : null"
                        v-translate
                    >At least 8 characters</li>
                    <li
                        :class="validations.atLeast1Capital ? 'li-valid' : null"
                        v-translate
                    >At least 1 capital letter</li>
                    <li
                        :class="validations.atLeast1Number ? 'li-valid' : null"
                        v-translate
                    >At least 1 number</li>
                    <li
                        :class="validations.atLeast1Symbol ? 'li-valid' : null"
                        v-translate
                    >At least 1 symbol</li>
                    <li
                        :class="validations.notMatchedPassword == 'interim' ? 'li-progress' : !validations.notMatchedPassword ? null : 'li-valid' ? 'li-valid' : null"
                        v-if="reset"
                        v-translate
                    >Not the same as your last password</li>
                </ul>
            </div>
        </div>

        <div
            class="form-group justify-content-center align-items-start"
            :class="{ 'form-row': formRow, 'row': !formRow }"
        >
            <div class="col-md-3">
                <label
                    for="password-confirm"
                    v-translate
                >Re-enter Password</label>
            </div>

            <div :class="passwordConfirmed ? 'col-11 col-md-6' : 'col-md-6'">
                <input
                    @keypress="checkInput"
                    v-validate="'required'"
                    :data-vv-as="'password confirmation' | translate"
                    id="password-confirm"
                    :type="pwordConfirmHidden ? 'password' : 'text'"
                    :class="`form-control ${passwordSubmitFailed ? 'is-invalid' : ''}`"
                    name="password_confirmation"
                    :disabled="isDisabled"
                    v-model="password_confirmation"
                    required
                >
                <InlineError
                    :errors="errors"
                    type="password_confirmation"
                />

                <div
                    tabindex="0"
                    class="eye-container"
                    :class="{ 'eye-container--disabled': isDisabled }"
                    @click="togglePasswordConfirmation"
                    @keyup.enter.space="togglePasswordConfirmation"
                    aria-label="Reveals or hides password"
                >
                    <object
                        tabindex="-1"
                        v-show="isDisabled"
                        data="/img/cancel.svg"
                        type="image/svg+xml"
                        aria-hidden="true"
                    ></object>
                    <object
                        tabindex="-1"
                        v-show="!isDisabled && pwordConfirmHidden"
                        data="/img/eye.svg"
                        type="image/svg+xml"
                        alt="Password hidden"
                    ></object>
                    <object
                        tabindex="-1"
                        v-show="!isDisabled && !pwordConfirmHidden"
                        data="/img/eye-filled.svg"
                        type="image/svg+xml"
                        alt="Password revealed"
                    ></object>
                </div>
            </div>

            <div class="password-confirm-valid-wrapper">
                <object
                    class="password-confirm-valid"
                    tabindex="-1"
                    v-show="passwordConfirmed"
                    data="/img/check-circle-green-chunky.svg"
                    type="image/svg+xml"
                    alt="Passwords match"
                ></object>
            </div>
        </div>
    </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import InlineError from '@C/Forms/InlineError';
import passwordValid from '@M/passwordValid';

export default {
    name: 'PasswordHandler',
    inject: ['$validator'],
    computed: {
        ...mapState(['formSubmitting']),
        ...mapFields('Auth', [
            'password',
            'password_confirmation',
            'passwordConfirmed',
            'pwordHidden',
            'pwordConfirmHidden',
            'passwordSubmitFailed'
        ]),
        isDisabled() {
            return this.total < 4 || this.formSubmitting;
        }
    },
    props: {
        reset: {
            type: Boolean,
            required: true,
            default: false
        },
        formRow: {
            type: Boolean,
            default: false
        },
        registration: Boolean,
        default: false
    },
    methods: {
        ...mapMutations({
            togglePassword: 'Auth/togglePassword',
            togglePasswordConfirmation: 'Auth/togglePasswordConfirmation'
        }),
        checkInput(e) {
            if (e.keyCode == 32) {
                e.preventDefault();
            }
        }
    },
    components: {
        InlineError
    },
    mixins: [passwordValid]
};
</script>




// WEBPACK FOOTER //
// resources/assets/js/components/PasswordHandler.vue